import { useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Group, Loader,
  Popover, Stack, Text,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconX, IconInfoCircle } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

interface Tenant {
  id: number
  name: string
  environment: string
}

interface TenantScopeData {
  tenant_ids: number[]
  tenants: Tenant[]
  source: 'admin_full' | 'user_template_tenants' | 'permission_template_tenants' | 'none'
  template_id: number | null
  template_name: string | null
}

const SOURCE_LABELS: Record<string, string> = {
  admin_full: 'Admin global',
  user_template_tenants: 'Scope individual',
  permission_template_tenants: 'Herdado do template',
  none: 'Sem template',
}

interface TenantScopeTabProps {
  userId: number
}

export function TenantScopeTab({ userId }: TenantScopeTabProps) {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [loadingAdd, setLoadingAdd] = useState<number | null>(null)
  const [loadingRemove, setLoadingRemove] = useState<number | null>(null)

  const { data: scope, isLoading: scopeLoading } = useQuery<TenantScopeData>({
    queryKey: ['tenant-scope', userId],
    queryFn: async () => {
      const res = await api.get(`/v1/users/${userId}/tenant-scope`)
      return res.data
    },
  })

  const { data: allTenantsData } = useQuery<{ items: Tenant[] }>({
    queryKey: ['tenants-all'],
    queryFn: async () => {
      const res = await api.get('/v1/admin/tenants')
      return res.data
    },
  })

  const allTenants = allTenantsData?.items ?? []

  const handleAdd = async (tenantId: number) => {
    setLoadingAdd(tenantId)
    try {
      await api.post(`/v1/users/${userId}/tenant-scope/add`, { tenant_id: tenantId })
      await qc.invalidateQueries({ queryKey: ['tenant-scope', userId] })
      setAddOpen(false)
      notifications.show({ message: 'Tenant adicionado', color: 'teal' })
    } catch (err: any) {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao adicionar.' })
    } finally {
      setLoadingAdd(null)
    }
  }

  const handleRemove = async (tenantId: number) => {
    setLoadingRemove(tenantId)
    try {
      await api.delete(`/v1/users/${userId}/tenant-scope/${tenantId}`)
      await qc.invalidateQueries({ queryKey: ['tenant-scope', userId] })
      notifications.show({ message: 'Tenant removido', color: 'teal' })
    } catch (err: any) {
      notifications.show({ color: 'red', title: 'Erro', message: err?.response?.data?.detail ?? 'Falha ao remover.' })
    } finally {
      setLoadingRemove(null)
    }
  }

  if (scopeLoading) return <Loader size="xs" mt="sm" />
  if (!scope) {
    return (
      <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />} mt="sm">
        Não foi possível carregar o escopo de tenants deste usuário.
      </Alert>
    )
  }

  const isReadonly = scope.source === 'admin_full'
  const availableToAdd = allTenants.filter(t => !scope.tenant_ids.includes(t.id))

  return (
    <Stack gap="sm" pt="xs">
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          {isReadonly ? 'Tenants: todos (admin global)' : `Tenants ativos: ${scope.tenant_ids.length}`}
        </Text>
        <Badge variant="outline" color="gray" size="xs">
          {SOURCE_LABELS[scope.source] ?? scope.source}
        </Badge>
      </Group>

      {isReadonly ? (
        <Text size="xs" c="dimmed">
          Administradores têm acesso automático a todos os tenants via admin:full.
        </Text>
      ) : (
        <Group gap="xs" wrap="wrap">
          {scope.tenants.map(t => (
            <Badge
              key={t.id}
              variant="light"
              color="teal"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  loading={loadingRemove === t.id}
                  onClick={() => handleRemove(t.id)}
                  aria-label={`Remover ${t.name}`}
                >
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              {t.name} — {t.environment}
            </Badge>
          ))}

          {availableToAdd.length > 0 && (
            <Popover opened={addOpen} onClose={() => setAddOpen(false)} withinPortal>
              <Popover.Target>
                <ActionIcon
                  variant="outline"
                  color="teal"
                  size="sm"
                  onClick={() => setAddOpen(v => !v)}
                  aria-label="Adicionar tenant"
                >
                  <IconPlus size={13} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap={4}>
                  {availableToAdd.map(t => (
                    <Button
                      key={t.id}
                      variant="subtle"
                      size="xs"
                      loading={loadingAdd === t.id}
                      onClick={() => handleAdd(t.id)}
                    >
                      {t.name} — {t.environment}
                    </Button>
                  ))}
                </Stack>
              </Popover.Dropdown>
            </Popover>
          )}
        </Group>
      )}
    </Stack>
  )
}
